// src/pages/Notebook.jsx
import { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  LinearProgress,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  MenuItem,
  Alert,
  Snackbar,
  Paper,
} from "@mui/material";
import { authHeaders, exportToGitHub, getGitHubStatus, getGitHubRepositories, getGitHubBranches, getInsights, getDecisionOutput, getRun, getUserSettings, getRunReport } from "../utils/api";
import BusinessInsights from "../components/BusinessInsights";
import BusinessRecommendation from "../components/BusinessRecommendation";
import BusinessDataSourcePanel from "../components/BusinessDataSourcePanel";
import RunDataView from "../components/RunDataView";
import decisionOutputSample from "../fixtures/decisionOutputSample.json";
import { parseDecisionOutputPayload } from "../utils/decisionFormatters";
import { API_BASE } from "../utils/env";
import {
  DEFAULT_RUN_DOMAIN,
  effectiveDomainSlugForNotebook,
  getDomainProfile,
  normalizeDomainSlugForProfile,
} from "../config/domainProfiles";
import { useMsal } from "@azure/msal-react";
import DownloadIcon from "@mui/icons-material/Download";
import GitHubIcon from "@mui/icons-material/GitHub";

function slugify(text) {
  return (text || "")
    .replace(/\u00B6/g, "")
    .replace(/\s*¶\s*$/, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function Notebook() {
  const { id } = useParams();
  const runId =
    id === "last"
      ? JSON.parse(sessionStorage.getItem("lastRunId") || "null")
      : id;

  const [view, setView] = useState("insights"); // "insights" | "live" | "work"
  const [notebookType, setNotebookType] = useState("training"); // "training" | "prediction"
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sections, setSections] = useState([]);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [decisionOutputData, setDecisionOutputData] = useState(null);
  const [decisionOutputLoading, setDecisionOutputLoading] = useState(true);
  const [runDataRefreshKey, setRunDataRefreshKey] = useState(0);
  /** After a queued CSV upload, hide recommendation until the ULP pipeline completes (Pilot Result insights stay below). */
  const [holdBusinessRecommendationAfterUpload, setHoldBusinessRecommendationAfterUpload] = useState(false);
  /** Resolved from GET /runs/:id (`domain`), aligned with backend. */
  const [runDomain, setRunDomain] = useState(DEFAULT_RUN_DOMAIN);
  /** From GET /runs/:id; null until first fetch completes. */
  const [businessOutputMode, setBusinessOutputMode] = useState(/** @type {string | null} */ (null));
  const [runMetaLoaded, setRunMetaLoaded] = useState(false);
  /** Firestore `users.{uid}.domain`, exposed as GET /settings → `user_domain`. */
  const [userProfileDomain, setUserProfileDomain] = useState("");
  /** When false, tenant may still come from user doc — do not force Technical yet. */
  const [userSettingsResolved, setUserSettingsResolved] = useState(false);
  const notebookRef = useRef(null);

  const { accounts } = useMsal();
  const emailHint =
    accounts[0]?.username ||
    accounts[0]?.idTokenClaims?.email ||
    accounts[0]?.idTokenClaims?.preferred_username ||
    "";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await getUserSettings();
        if (!alive) return;
        const u = s?.user_domain ?? s?.domain;
        setUserProfileDomain(typeof u === "string" ? u : "");
      } catch {
        if (alive) setUserProfileDomain("");
      } finally {
        if (alive) setUserSettingsResolved(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /** Run.domain, else user.domain from settings, else email host if it maps to a tenant client. */
  const notebookProfileDomain = useMemo(
    () => effectiveDomainSlugForNotebook(runDomain, userProfileDomain, emailHint),
    [runDomain, userProfileDomain, emailHint]
  );
  const domainProfile = useMemo(() => getDomainProfile(notebookProfileDomain), [notebookProfileDomain]);
  /** From client profile (e.g. cpWealth): Business + Technical tabs and business column with data source. */
  const businessNotebookEnabled = runMetaLoaded && domainProfile.supportsDecisionProfile;
  /** Loads decision_output / insights; off when `business_output_mode` is `none`. */
  const decisionArtifactsEnabled =
    businessNotebookEnabled && businessOutputMode !== "none";

  // GitHub export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRepoOwner, setExportRepoOwner] = useState("");
  const [exportRepoName, setExportRepoName] = useState("");
  const [exportRepoPath, setExportRepoPath] = useState("");
  const [exportBranch, setExportBranch] = useState("main");
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState("");
  const [githubConnected, setGithubConnected] = useState(false);
  const [githubLogin, setGithubLogin] = useState("");
  const [repositories, setRepositories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  /** Fetched HTML string from GCS report.html, or null if not present. */
  const [reportHtml, setReportHtml] = useState(null);
  /** True once the report.html check for the current run has settled (found or not found). */
  const [reportHtmlChecked, setReportHtmlChecked] = useState(false);

  useEffect(() => {
    setView("insights");
  }, [id]);

  useEffect(() => {
    setHoldBusinessRecommendationAfterUpload(false);
  }, [runId]);

  // Reset report state whenever the run changes so stale content is never shown.
  useEffect(() => {
    setReportHtml(null);
    setReportHtmlChecked(false);
  }, [runId]);

  // Fetch report.html from GCS once the run's business profile is confirmed.
  // Falls back to null (structured view) on 404 or any error.
  useEffect(() => {
    if (!runId || !businessNotebookEnabled) return;
    let alive = true;
    (async () => {
      try {
        const html = await getRunReport(runId);
        if (alive) setReportHtml(html);
      } catch {
        if (alive) setReportHtml(null);
      } finally {
        if (alive) setReportHtmlChecked(true);
      }
    })();
    return () => { alive = false; };
  }, [runId, businessNotebookEnabled]);

  const prevHoldBusinessRecRef = useRef(holdBusinessRecommendationAfterUpload);
  useEffect(() => {
    if (prevHoldBusinessRecRef.current && !holdBusinessRecommendationAfterUpload) {
      setRunDataRefreshKey((k) => k + 1);
    }
    prevHoldBusinessRecRef.current = holdBusinessRecommendationAfterUpload;
  }, [holdBusinessRecommendationAfterUpload]);

  // Load run.domain + business_output_mode (server defaults legacy runs to standard domain + mode).
  useEffect(() => {
    if (!runId) {
      setRunDomain(DEFAULT_RUN_DOMAIN);
      setBusinessOutputMode(null);
      setRunMetaLoaded(false);
      return;
    }
    let alive = true;
    setRunMetaLoaded(false);
    (async () => {
      let metaSlug = DEFAULT_RUN_DOMAIN;
      let metaMode = "standard";
      try {
        const doc = await getRun(runId);
        if (!alive) return;
        metaSlug = normalizeDomainSlugForProfile(doc?.domain);
        metaMode = doc?.business_output_mode === "none" ? "none" : "standard";
        setRunDomain(metaSlug);
        setBusinessOutputMode(metaMode);
      } catch {
        if (alive) {
          metaSlug = DEFAULT_RUN_DOMAIN;
          metaMode = "standard";
          setRunDomain(metaSlug);
          setBusinessOutputMode(metaMode);
        }
      } finally {
        if (alive) setRunMetaLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [runId]);

  // Wait for GET /settings so `user_domain` can upgrade the profile; otherwise we briefly think
  // `default` and force Work, then never return to Insights when user.domain is cp-wealth.net.
  useEffect(() => {
    if (!runMetaLoaded || !runId || !userSettingsResolved) return;
    if (!domainProfile.supportsDecisionProfile) { setView("work"); return; }
    const hasLive = domainProfile.showBusinessDataSourcePanel || domainProfile.dailyTickerPredictionsTable;
    if (hasLive) setView("live");
  }, [runMetaLoaded, runId, userSettingsResolved, domainProfile.supportsDecisionProfile, domainProfile.showBusinessDataSourcePanel, domainProfile.dailyTickerPredictionsTable]);

  const useDecisionSample = process.env.REACT_APP_USE_DECISION_SAMPLE === "true";
  // Firestore string: opaque JSON text (parse here). Firestore map: API returns object (legacy).
  const decisionOutput =
    !runMetaLoaded || !decisionArtifactsEnabled
      ? null
      : parseDecisionOutputPayload(decisionOutputData) ??
        (useDecisionSample ? decisionOutputSample : null);

  const hideLowerDataGrid =
    decisionOutput != null &&
    typeof decisionOutput === "object" &&
    !Array.isArray(decisionOutput) &&
    decisionOutput.user_live_prediction_display != null &&
    typeof decisionOutput.user_live_prediction_display === "object" &&
    !Array.isArray(decisionOutput.user_live_prediction_display) &&
    typeof decisionOutput.user_live_prediction_display.bq_table === "string" &&
    decisionOutput.user_live_prediction_display.bq_table.trim().length > 0;

  // === Fetch decision_output (same pattern as insights: dedicated GET + optional stub on server) ===
  useEffect(() => {
    if (!runId) {
      setDecisionOutputData(null);
      setDecisionOutputLoading(false);
      return;
    }
    if (!runMetaLoaded) {
      setDecisionOutputLoading(true);
      return;
    }
    if (!decisionArtifactsEnabled) {
      setDecisionOutputData(null);
      setDecisionOutputLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setDecisionOutputLoading(true);
      try {
        const data = await getDecisionOutput(runId);
        if (alive) setDecisionOutputData(data);
      } catch (primaryErr) {
        // Dedicated route may be missing on older deploys; GET /runs/:id still exposes decision_output via _sanitize_run.
        console.warn("[Notebook] getDecisionOutput failed, falling back to getRun:", primaryErr?.message || primaryErr);
        try {
          const runDoc = await getRun(runId);
          const fromRun = runDoc?.decision_output ?? runDoc?.decisionOutput;
          if (alive) setDecisionOutputData(fromRun ?? null);
        } catch (fallbackErr) {
          console.error("[Notebook] getRun fallback failed:", fallbackErr?.message || fallbackErr);
          if (alive) setDecisionOutputData(null);
        }
      } finally {
        if (alive) setDecisionOutputLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [runId, runDataRefreshKey, runMetaLoaded, decisionArtifactsEnabled]);

  // === Fetch insights (business decisions) ===
  useEffect(() => {
    if (!runId) {
      setInsightsLoading(false);
      return;
    }
    if (!runMetaLoaded) {
      setInsightsLoading(true);
      return;
    }
    if (!decisionArtifactsEnabled) {
      setInsights(null);
      setInsightsLoading(false);
      return;
    }
    let alive = true;
    let pollInterval = null;

    async function fetchInsights() {
      try {
        const data = await getInsights(runId);
        if (!alive) return;
        setInsights(data);
        setInsightsLoading(false);
        const impactStatus = data?.impact?.status;
        if (impactStatus === "pending" || impactStatus === "running") {
          pollInterval = setInterval(async () => {
            try {
              const updated = await getInsights(runId);
              if (!alive) return;
              setInsights(updated);
              const newStatus = updated?.impact?.status;
              if (newStatus !== "pending" && newStatus !== "running") {
                clearInterval(pollInterval);
              }
            } catch {
              clearInterval(pollInterval);
            }
          }, 8000);
        }
      } catch {
        if (alive) {
          setInsights(null);
          setInsightsLoading(false);
        }
      }
    }

    fetchInsights();
    return () => {
      alive = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [runId, runMetaLoaded, decisionArtifactsEnabled]);

  // === Fetch notebook content (work view only) ===
  useEffect(() => {
    if (view !== "work") return;
    let alive = true;
    async function load() {
      setErr("");
      setHtml("");
      if (!runId) { setLoading(false); return; }
      setLoading(true);
      try {
        const headers = await authHeaders();
        const params = new URLSearchParams();
        params.append("format", "html");
        if (notebookType === "prediction") params.append("type", "prediction");
        const res = await fetch(
          `${API_BASE}/runs/${encodeURIComponent(runId)}/notebook?${params.toString()}`,
          { headers }
        );
        if (!res.ok) {
          if (res.status === 404 && notebookType === "prediction") {
            try {
              const errorData = await res.json();
              throw new Error(errorData.message || "Prediction notebook not yet available. Please complete the training process first, or this run was generated before live prediction capability was available.");
            } catch {
              throw new Error("Prediction notebook not yet available. Please complete the training process first, or this run was generated before live prediction capability was available.");
            }
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const text = await res.text();
        if (alive) setHtml(text);
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load notebook");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [runId, view, notebookType]);

  // === Extract notebook outline from rendered HTML ===
  useEffect(() => {
    if (!html) { setSections([]); return; }
    const root = notebookRef.current;
    if (!root) return;
    const headers = Array.from(root.querySelectorAll("h1, h2, h3"));
    const seen = Object.create(null);
    const items = headers
      .map((el, index) => {
        const text = el.textContent.replace("¶", "").trim();
        if (!text) return null;
        const baseId = slugify(text) || `header-${index}`;
        let id = el.id && !seen[el.id] ? el.id : baseId;
        const count = (seen[id] || 0) + 1;
        seen[id] = count;
        if (count > 1) id = `${id}-${count}`;
        el.id = id;
        const anchor = el.querySelector("a.anchor-link, a[href^='#']");
        if (anchor) anchor.setAttribute("href", `#${id}`);
        return { id, text, tag: el.tagName };
      })
      .filter(Boolean);
    const anchors = root.querySelectorAll("a.anchor-link, a[href^='#']");
    anchors.forEach((a) => {
      const h = a.closest("h1,h2,h3");
      if (h && h.id) {
        a.setAttribute("href", `#${h.id}`);
        const handler = (e) => {
          e.preventDefault();
          document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        };
        a.addEventListener("click", handler);
        a._handler = handler;
      }
    });
    setSections(items);
    return () => {
      anchors.forEach((a) => {
        if (a._handler) { a.removeEventListener("click", a._handler); delete a._handler; }
      });
    };
  }, [html, notebookType]);

  // Check GitHub connection status
  useEffect(() => {
    const checkGitHub = async () => {
      try {
        const status = await getGitHubStatus();
        setGithubConnected(status?.connected || false);
        setGithubLogin(status?.github_login || "");
        if (status?.connected && status?.github_login) {
          setExportRepoOwner(status.github_login);
        }
      } catch (e) {
        console.error("Failed to check GitHub status:", e);
        setGithubConnected(false);
      }
    };
    checkGitHub();
  }, []);

  // Load repositories when dialog opens
  useEffect(() => {
    if (exportDialogOpen && githubConnected) {
      const loadRepos = async () => {
        setLoadingRepos(true);
        try {
          const data = await getGitHubRepositories();
          setRepositories(data?.repositories || []);
        } catch (e) {
          console.error("Failed to load repositories:", e);
          setRepositories([]);
        } finally {
          setLoadingRepos(false);
        }
      };
      loadRepos();
    }
  }, [exportDialogOpen, githubConnected]);

  // Load branches when repository is selected
  useEffect(() => {
    if (exportRepoOwner && exportRepoName && githubConnected) {
      const loadBranches = async () => {
        setLoadingBranches(true);
        try {
          const data = await getGitHubBranches(exportRepoOwner, exportRepoName);
          const branchList = data?.branches || [];
          setBranches(branchList);
          if (branchList.length > 0) {
            const mainBranch = branchList.find(b => b === "main") || branchList.find(b => b === "master") || branchList[0];
            setExportBranch(mainBranch);
          } else {
            setExportBranch("");
          }
        } catch (e) {
          setBranches([]);
          setExportBranch("");
          setExportError(`Failed to load branches: ${e?.response?.data?.error || e?.message || "Unknown error"}`);
        } finally {
          setLoadingBranches(false);
        }
      };
      loadBranches();
    } else {
      setBranches([]);
      setExportBranch("");
    }
  }, [exportRepoOwner, exportRepoName, githubConnected]);

  // Auto-suggest file path
  useEffect(() => {
    if (runId && exportRepoName && !exportRepoPath) {
      setExportRepoPath(`notebooks/run-${runId}.ipynb`);
    }
  }, [runId, exportRepoName, exportRepoPath]);

  const handleExportToGitHub = async () => {
    if (!runId) return;
    try {
      setExporting(true);
      setExportError("");
      await exportToGitHub({
        runId,
        repoOwner: exportRepoOwner,
        repoName: exportRepoName,
        repoPath: exportRepoPath || `notebooks/run-${runId}.ipynb`,
        branch: exportBranch,
        type: "notebook",
      });
      setExportSuccess(true);
      setExportDialogOpen(false);
      setExportRepoName("");
      setExportRepoPath("");
      setExportBranch("main");
      setBranches([]);
    } catch (e) {
      setExportError(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const drawerWidth = 260;
  const containerClass = "notebook-rendered";

  const handleDownload = async () => {
    if (!runId) return;
    try {
      const headers = await authHeaders();
      const params = new URLSearchParams();
      if (notebookType === "prediction") params.append("type", "prediction");
      const res = await fetch(
        `${API_BASE}/runs/${encodeURIComponent(runId)}/notebook?${params.toString()}`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("content-disposition") || "";
      let filename = (cd.match(/filename\*=UTF-8''([^;]+)$/i)?.[1]
        || cd.match(/filename="?([^";]+)"?/i)?.[1]
        || `run-${runId}.ipynb`);
      try { filename = decodeURIComponent(filename); } catch {}
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download notebook", e);
      setErr(e.message || "Failed to download notebook");
    }
  };

  return (
    <Box sx={{ display: "flex", flexGrow: 1 }}>
      {/* --- Main Content --- */}
      <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
        {/* --- Toolbar --- */}
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            value={view}
            exclusive
            onChange={(_, v) => v && setView(v)}
          >
            {businessNotebookEnabled && (
              <ToggleButton value="insights">Insights</ToggleButton>
            )}
            {(domainProfile.showBusinessDataSourcePanel || domainProfile.dailyTickerPredictionsTable) && (
              <ToggleButton value="live">Live</ToggleButton>
            )}
            <ToggleButton value="work">Work</ToggleButton>
          </ToggleButtonGroup>
          {view === "work" && (
            <ToggleButtonGroup
              size="small"
              value={notebookType}
              exclusive
              onChange={(_, v) => v && setNotebookType(v)}
            >
              <ToggleButton value="training">Training</ToggleButton>
              <ToggleButton value="prediction">Prediction</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Box sx={{ flex: 1 }} />
          {view === "work" && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={!runId}
              >
                Download .ipynb
              </Button>
              {githubConnected && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  onClick={() => setExportDialogOpen(true)}
                  disabled={!runId}
                >
                  Export to GitHub
                </Button>
              )}
            </>
          )}
        </Box>

        {/* --- Insights tab --- */}
        {view === "insights" && runId && !runMetaLoaded && (
          <>
            <LinearProgress sx={{ mb: 1 }} />
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={4}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Loading run…</Typography>
            </Box>
          </>
        )}
        {businessNotebookEnabled && view === "insights" && runMetaLoaded && !reportHtmlChecked && (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={4}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Loading report…</Typography>
          </Box>
        )}
        {businessNotebookEnabled && view === "insights" && runMetaLoaded && reportHtmlChecked && reportHtml && (
          <Box sx={{ width: "100%", height: "calc(100vh - 120px)" }}>
            <iframe
              srcDoc={reportHtml}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Business Report"
            />
          </Box>
        )}
        {businessNotebookEnabled && view === "insights" && runMetaLoaded && reportHtmlChecked && !reportHtml && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <Typography color="text.secondary">Pilot report not yet available.</Typography>
          </Box>
        )}

        {/* --- Live tab --- */}
        {view === "live" && (
          <Stack spacing={3} sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
            {domainProfile.showBusinessDataSourcePanel && (
              <BusinessDataSourcePanel
                runId={runId}
                onUploadSuccess={() => setRunDataRefreshKey((k) => k + 1)}
                onBusinessFrontStaleHoldChange={setHoldBusinessRecommendationAfterUpload}
              />
            )}
            {!holdBusinessRecommendationAfterUpload && (
              <BusinessRecommendation
                decisionOutput={decisionOutput}
                loading={decisionOutputLoading}
                runId={runId}
                dataRefreshKey={runDataRefreshKey}
                domainProfile={domainProfile}
              />
            )}
            <BusinessInsights insights={insights} loading={insightsLoading} domainProfile={domainProfile} />
            {domainProfile.dailyTickerPredictionsTable && (
              <Box
                id="notebook-daily-ticker-predictions"
                sx={{ width: "100%", overflow: "hidden", minWidth: 0, maxWidth: "100%", scrollMarginTop: 72 }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Current Portfolio Prediction
                </Typography>
                <RunDataView
                  runId={runId}
                  tableName={domainProfile.dailyTickerPredictionsTable}
                  hideTableSelector
                  refreshKey={runDataRefreshKey}
                  columnOrder={["security", "source", "adj_close_date", "price", "quantity", "net_amount", "percentile"]}
                  columnExclusion={["prediction", "investment_amount", "prediction_run_date", "date"]}
                />
              </Box>
            )}
            {!hideLowerDataGrid && (
              <Box
                id="notebook-detailed-data"
                sx={{ width: "100%", overflow: "hidden", minWidth: 0, maxWidth: "100%", scrollMarginTop: 72 }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Detailed data
                </Typography>
                <RunDataView runId={runId} hideTableSelector refreshKey={runDataRefreshKey} />
              </Box>
            )}
          </Stack>
        )}

        {/* --- Work tab (notebooks) --- */}
        {view === "work" && (
          <>
            {loading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} /> <Typography>Loading…</Typography>
              </Box>
            )}
            {!loading && err && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: notebookType === "prediction" ? "info.light" : "error.light",
                  borderRadius: 2,
                  border: `1px solid ${notebookType === "prediction" ? "info.main" : "error.main"}`,
                }}
              >
                <Alert
                  severity={notebookType === "prediction" ? "info" : "error"}
                  sx={{ backgroundColor: "transparent", p: 0 }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {notebookType === "prediction" ? "Prediction Notebook Not Available" : "Error Loading Notebook"}
                  </Typography>
                  <Typography variant="body2">{err}</Typography>
                </Alert>
              </Paper>
            )}
            {!loading && !err && html && (
              <>
                <style>{`
                  .${containerClass} pre, .${containerClass} code { white-space: pre; }
                  .${containerClass} pre { overflow-x: auto; }
                  .${containerClass} table { display: block; overflow-x: auto; white-space: nowrap; }
                `}</style>
                <div
                  ref={notebookRef}
                  className={containerClass}
                  style={{ border: "1px solid #eee", borderRadius: 8, padding: 16, overflowX: "auto" }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </>
            )}
          </>
        )}
      </Box>

      {/* --- TOC Sidebar (work view only) --- */}
      {view === "work" && html && (
        <Drawer
          anchor="right"
          variant="permanent"
          open
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderLeft: "1px solid #eee",
              p: 2,
              top: 64,
              height: "calc(100% - 64px)",
              overflowY: "auto",
            },
          }}
        >
          <Typography variant="h6" fontWeight={600}>Table of Contents</Typography>
          <Divider sx={{ mb: 1 }} />
          {sections.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              (No headings found)
            </Typography>
          ) : (
            <List dense>
              {sections.map((s, i) => (
                <ListItemButton key={i} onClick={() => scrollTo(s.id)}>
                  <ListItemText
                    primary={s.text}
                    primaryTypographyProps={{
                      variant: s.tag === "H1" ? "body1" : s.tag === "H2" ? "body2" : "caption",
                      sx: {
                        pl: s.tag === "H3" ? 2 : s.tag === "H2" ? 1 : 0,
                        fontWeight: s.tag === "H1" ? 600 : 400,
                        color: s.tag === "H1" ? "text.primary" : s.tag === "H2" ? "text.secondary" : "text.disabled",
                        whiteSpace: "normal",
                        overflow: "visible",
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Drawer>
      )}

      {/* --- Export to GitHub Dialog --- */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Notebook to GitHub</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {exportError && (
              <Alert severity="error" onClose={() => setExportError("")}>{exportError}</Alert>
            )}
            <TextField
              label="Repository Owner"
              value={exportRepoOwner}
              onChange={(e) => { setExportRepoOwner(e.target.value); setExportRepoName(""); setBranches([]); }}
              placeholder="username or org"
              fullWidth
              required
              helperText={githubLogin ? `Connected as ${githubLogin}` : ""}
            />
            <TextField
              label="Repository"
              value={(() => {
                const matchedRepo = repositories.find(r => r.name === exportRepoName && r.owner === exportRepoOwner);
                return matchedRepo ? matchedRepo.full_name : exportRepoName;
              })()}
              onChange={(e) => {
                const value = e.target.value;
                const selectedByFullName = repositories.find(r => r.full_name === value);
                if (selectedByFullName) { setExportRepoOwner(selectedByFullName.owner); setExportRepoName(selectedByFullName.name); return; }
                const matchByOwner = repositories.find(r => r.name === value && r.owner === exportRepoOwner);
                if (matchByOwner) { setExportRepoName(matchByOwner.name); return; }
                const matchByName = repositories.find(r => r.name === value);
                if (matchByName) { setExportRepoOwner(matchByName.owner); setExportRepoName(matchByName.name); return; }
                setExportRepoName(value);
              }}
              select={repositories.length > 0}
              fullWidth
              required
              disabled={loadingRepos}
              helperText={
                loadingRepos ? "Loading repositories..."
                : repositories.length > 0 ? `Select from ${repositories.length} repositories or type repository name`
                : "No repositories found"
              }
            >
              {repositories.map((repo) => (
                <MenuItem key={repo.full_name} value={repo.full_name}>
                  {repo.full_name} {repo.private ? "🔒" : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="File Path"
              value={exportRepoPath}
              onChange={(e) => setExportRepoPath(e.target.value)}
              placeholder={`notebooks/run-${runId}.ipynb`}
              fullWidth
              helperText="Path where the file will be created/updated"
            />
            <TextField
              label="Branch"
              value={exportBranch}
              onChange={(e) => setExportBranch(e.target.value)}
              select
              fullWidth
              disabled={loadingBranches || !exportRepoOwner || !exportRepoName}
              helperText={
                loadingBranches ? "Loading branches..."
                : !exportRepoOwner || !exportRepoName ? "Select repository first"
                : branches.length === 0 ? "No branches found or access denied"
                : `${branches.length} branch(es) available`
              }
            >
              {branches.map((branch) => (
                <MenuItem key={branch} value={branch}>{branch}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>Cancel</Button>
          <Button onClick={handleExportToGitHub} variant="contained" disabled={exporting || !exportRepoOwner || !exportRepoName}>
            {exporting ? "Exporting..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Success Snackbar --- */}
      <Snackbar
        open={exportSuccess}
        autoHideDuration={6000}
        onClose={() => setExportSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setExportSuccess(false)} severity="success" variant="filled">
          Notebook exported to GitHub successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
}
